/**
 * Encodes an i18n key (+ optional params) as a plain string so shared server/client
 * modules stay free of react-i18next. The UI decodes with `translateI18nError`.
 *
 * Separator is Unit Separator (U+001F): it never appears in keys or JSON of our params.
 */
const SEP = '\u001f';

export function i18nError(key: string, params?: Record<string, string | number>): string {
  if (!params || Object.keys(params).length === 0) return key;
  return `${key}${SEP}${JSON.stringify(params)}`;
}

export function parseI18nError(raw: string): { key: string; params?: Record<string, string | number> } | null {
  if (!raw) return null;
  const sep = raw.indexOf(SEP);
  const key = sep === -1 ? raw : raw.slice(0, sep);
  // At least one namespace dot, so free-text carrier messages and labels like "Proveedor" stay as-is.
  if (!/^[a-zA-Z][a-zA-Z0-9]*(?:\.[a-zA-Z0-9_]+)+$/.test(key)) return null;
  if (sep === -1) return { key };
  try {
    const params = JSON.parse(raw.slice(sep + 1));
    if (params && typeof params === 'object' && !Array.isArray(params)) {
      return { key, params };
    }
  } catch {
    return { key };
  }
  return { key };
}

export function translateI18nError(
  raw: string | undefined | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (raw == null || raw === '') return '';
  const parsed = parseI18nError(raw);
  if (!parsed) return raw;
  const params: Record<string, unknown> = { ...(parsed.params || {}) };
  for (const [k, v] of Object.entries(params)) {
    if (k === 'pattern' || k === 'whichKey') continue;
    if (typeof v === 'string' && v.includes(SEP)) params[k] = translateI18nError(v, t);
  }
  if (typeof params.pattern === 'string') params.description = t(params.pattern);
  if (typeof params.whichKey === 'string') params.which = t(params.whichKey);
  return t(parsed.key, params);
}
